import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Activity, RefreshCw, Database, BarChart3, Table,
  TrendingUp, TrendingDown, Minus, Upload, Trash2, Info
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Archetype definitions with SEPARATE Quality and Timing signals
// Quality = Is this a good alpha pattern? (drives card COLOR)
// Timing = When should you enter? (drives text label)
const ARCHETYPE_DEFINITIONS = {
  0: {
    name: "Front-Run Exhaustion",
    behavior: "Strong early performance, then decay",
    quality: "strong",
    timing: "Enter early",
  },
  1: {
    name: "Compounding Drift",
    behavior: "Smooth, steady upward slope",
    quality: "strong",
    timing: "Flexible",
  },
  2: {
    name: "Shakeout Recovery",
    behavior: "Initial drop, strong rebound",
    quality: "conditional",
    timing: "After dip",
  },
  3: {
    name: "Overcrowded Peak",
    behavior: "Mid-period peak, sharp decline",
    quality: "weak",
    timing: "Avoid",
  },
  4: {
    name: "Delayed Inflection",
    behavior: "Weak start, strong late acceleration",
    quality: "strong",
    timing: "Wait then enter",
  },
  5: {
    name: "Momentum Lock-In",
    behavior: "Fast rise, holds gains",
    quality: "top_tier",
    timing: "Enter early, hold",
  },
  6: {
    name: "Late Ignition",
    behavior: "Flat, sudden strong breakout",
    quality: "strong",
    timing: "Before breakout",
  },
  7: {
    name: "Distribution Phase",
    behavior: "Continuous decline after buy",
    quality: "weak",
    timing: "Avoid",
  },
  8: {
    name: "Slow Build Momentum",
    behavior: "Gradual acceleration over time",
    quality: "strong",
    timing: "Early + patience",
  },
};

// Quality styling (drives card COLOR)
const QUALITY_STYLES = {
  top_tier: { 
    label: "Top Tier", 
    bg: "bg-emerald-500/10", 
    border: "border-emerald-500/30", 
    text: "text-emerald-400",
    dot: "bg-emerald-500",
    chartColor: "#10b981"
  },
  strong: { 
    label: "Strong", 
    bg: "bg-green-500/5", 
    border: "border-green-500/20", 
    text: "text-green-400",
    dot: "bg-green-500",
    chartColor: "#22c55e"
  },
  conditional: { 
    label: "Conditional", 
    bg: "bg-amber-500/5", 
    border: "border-amber-500/20", 
    text: "text-amber-400",
    dot: "bg-amber-500",
    chartColor: "#f59e0b"
  },
  weak: { 
    label: "Weak", 
    bg: "bg-red-500/5", 
    border: "border-red-500/20", 
    text: "text-red-400",
    dot: "bg-red-500",
    chartColor: "#ef4444"
  },
};

const getArchetypeInfo = (clusterId) => {
  return ARCHETYPE_DEFINITIONS[clusterId] || {
    name: `Cluster ${clusterId}`,
    behavior: "Unknown pattern",
    quality: "conditional",
    timing: "Unknown",
  };
};

// Simple line chart component for curve visualization
const CurveChart = ({ curve, color = "#3b82f6", height = 120, showLabels = true }) => {
  if (!curve || curve.length === 0) return null;
  
  const width = 280;
  const padding = { top: 10, right: 10, bottom: showLabels ? 25 : 10, left: showLabels ? 35 : 10 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  const minVal = Math.min(...curve);
  const maxVal = Math.max(...curve);
  const range = maxVal - minVal || 1;
  
  const points = curve.map((val, idx) => {
    const x = padding.left + (idx / (curve.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((val - minVal) / range) * chartHeight;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      {/* Grid lines */}
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#e5e7eb" strokeWidth="1" />
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#e5e7eb" strokeWidth="1" />
      
      {/* Y-axis labels */}
      {showLabels && (
        <>
          <text x={padding.left - 5} y={padding.top + 5} fontSize="10" textAnchor="end" fill="#6b7280">{maxVal.toFixed(2)}</text>
          <text x={padding.left - 5} y={height - padding.bottom} fontSize="10" textAnchor="end" fill="#6b7280">{minVal.toFixed(2)}</text>
        </>
      )}
      
      {/* X-axis labels */}
      {showLabels && (
        <>
          <text x={padding.left} y={height - 5} fontSize="10" textAnchor="middle" fill="#6b7280">M1</text>
          <text x={padding.left + chartWidth / 2} y={height - 5} fontSize="10" textAnchor="middle" fill="#6b7280">M6</text>
          <text x={width - padding.right} y={height - 5} fontSize="10" textAnchor="middle" fill="#6b7280">M12</text>
        </>
      )}
      
      {/* Area fill */}
      <polygon 
        points={`${padding.left},${height - padding.bottom} ${points} ${width - padding.right},${height - padding.bottom}`}
        fill={color}
        fillOpacity="0.1"
      />
      
      {/* Line */}
      <polyline 
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Points */}
      {curve.map((val, idx) => {
        const x = padding.left + (idx / (curve.length - 1)) * chartWidth;
        const y = padding.top + chartHeight - ((val - minVal) / range) * chartHeight;
        return (
          <circle key={idx} cx={x} cy={y} r="3" fill={color} />
        );
      })}
    </svg>
  );
};

export default function CurveModelPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingModel, setLoadingModel] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [archetypes, setArchetypes] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Fetch model stats and archetypes
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch metadata
      const metaRes = await fetch(`${API}/curve-model/metadata?version=v1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (metaRes.ok) {
        const data = await metaRes.json();
        setMetadata(data);
      } else {
        setMetadata(null);
      }
      
      // Fetch archetypes
      const sortParam = sortBy ? `&sort_by=${sortBy}` : "";
      const archRes = await fetch(`${API}/curve-model/archetypes?version=v1${sortParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (archRes.ok) {
        const data = await archRes.json();
        setArchetypes(data.archetypes || []);
      } else {
        setArchetypes([]);
      }
      
    } catch (error) {
      console.error("Failed to fetch curve model data:", error);
    } finally {
      setLoading(false);
    }
  }, [token, sortBy]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Load model from pickle file
  const handleLoadModel = async () => {
    try {
      setLoadingModel(true);
      const response = await fetch(`${API}/curve-model/load?version=v1`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Model loaded: ${result.n_clusters} archetypes stored`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to load model");
      }
    } catch (error) {
      toast.error("Failed to load model");
    } finally {
      setLoadingModel(false);
    }
  };
  
  // Delete model
  const handleDeleteModel = async () => {
    if (!window.confirm("Are you sure you want to delete the curve model?")) return;
    
    try {
      const response = await fetch(`${API}/curve-model/delete/v1`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Model deleted");
        setMetadata(null);
        setArchetypes([]);
      } else {
        toast.error("Failed to delete model");
      }
    } catch (error) {
      toast.error("Failed to delete model");
    }
  };
  
  return (
    <div className="space-y-6" data-testid="curve-model-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Curve Model Viewer</h1>
          <p className="text-muted-foreground">
            Inspect the pre-trained archetypal curve model (read-only)
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {!metadata && (
            <Button 
              onClick={handleLoadModel}
              disabled={loadingModel}
              data-testid="load-model-btn"
            >
              <Upload className={`w-4 h-4 mr-2 ${loadingModel ? 'animate-spin' : ''}`} />
              Load Model
            </Button>
          )}
          {metadata && (
            <Button 
              variant="destructive"
              onClick={handleDeleteModel}
              data-testid="delete-model-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>
      
      {/* Model not loaded state */}
      {!metadata && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Model Loaded</h3>
            <p className="text-muted-foreground text-center mb-4">
              Click "Load Model" to load curve_model.pkl and inspect the 9 archetypal curves.
            </p>
            <Button onClick={handleLoadModel} disabled={loadingModel}>
              <Upload className="w-4 h-4 mr-2" />
              Load curve_model.pkl
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Model loaded - show tabs */}
      {metadata && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="curves" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Curves
            </TabsTrigger>
            <TabsTrigger value="table" className="flex items-center gap-2">
              <Table className="w-4 h-4" />
              Table
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Clusters</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metadata.n_clusters}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Training Samples</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metadata.n_training_samples?.toLocaleString()}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Inertia</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metadata.inertia?.toFixed(2)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground">Iterations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metadata.n_iter}</div>
                </CardContent>
              </Card>
            </div>
            
            {/* Quality-based grouping */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Curve Quality Summary</CardTitle>
                <CardDescription>
                  Color indicates curve quality. Timing shown as text.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {/* Strong Quality */}
                  <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <h4 className="font-medium text-green-400">Strong Alpha</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex justify-between">
                        <span>Front-Run Exhaustion</span>
                        <span className="text-xs">Enter early</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Compounding Drift</span>
                        <span className="text-xs">Flexible</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Delayed Inflection</span>
                        <span className="text-xs">Wait then enter</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Momentum Lock-In</span>
                        <span className="text-xs text-emerald-400">Top tier</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Late Ignition</span>
                        <span className="text-xs">Before breakout</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Slow Build Momentum</span>
                        <span className="text-xs">Early + patience</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Conditional Quality */}
                  <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      <h4 className="font-medium text-amber-400">Conditional</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex justify-between">
                        <span>Shakeout Recovery</span>
                        <span className="text-xs">After dip</span>
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3">
                      Good outcome if timed correctly
                    </p>
                  </div>
                  
                  {/* Weak Quality */}
                  <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <h4 className="font-medium text-red-400">Structurally Weak</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex justify-between">
                        <span>Overcrowded Peak</span>
                        <span className="text-xs">Avoid</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Distribution Phase</span>
                        <span className="text-xs">Avoid</span>
                      </li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3">
                      Smart money distribution
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Model Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Metric:</span>
                      <span className="ml-2 font-mono">{metadata.metric}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Gamma:</span>
                      <span className="ml-2 font-mono">{metadata.metric_params?.gamma}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Max Iter:</span>
                      <span className="ml-2 font-mono">{metadata.max_iter}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Init:</span>
                      <span className="ml-2 font-mono">{metadata.init}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Tolerance:</span>
                      <span className="ml-2 font-mono">{metadata.tol}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Model Version:</span>
                      <span className="ml-2 font-mono">{metadata.model_version}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cluster Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {metadata.cluster_distribution && Object.entries(metadata.cluster_distribution).map(([clusterId, count]) => {
                      const archInfo = getArchetypeInfo(parseInt(clusterId));
                      const qualityStyle = QUALITY_STYLES[archInfo.quality];
                      return (
                        <Badge key={clusterId} variant="outline" className={`text-sm ${qualityStyle.border}`}>
                          <span className={`w-2 h-2 rounded-full ${qualityStyle.dot} mr-1.5`}></span>
                          {archInfo.name}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Curves Tab */}
          <TabsContent value="curves" className="space-y-4">
            {/* Legend */}
            <div className="flex items-center gap-6 mb-4 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Quality:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span>Top Tier</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span>Strong</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span>Conditional</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span>Weak</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <div className="flex gap-2">
                {[
                  { value: null, label: "Cluster ID" },
                  { value: "m12", label: "Final Value" },
                  { value: "slope", label: "Overall Slope" },
                  { value: "early_slope", label: "Early Slope" },
                  { value: "late_slope", label: "Late Slope" },
                ].map((opt) => (
                  <Button
                    key={opt.value || "default"}
                    variant={sortBy === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSortBy(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              {archetypes.map((arch) => {
                const archInfo = getArchetypeInfo(arch.cluster_id);
                const qualityStyle = QUALITY_STYLES[archInfo.quality];
                return (
                  <Card key={arch.cluster_id} className={`overflow-hidden border ${qualityStyle.border}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${qualityStyle.dot}`}></div>
                          <span className="text-xs text-muted-foreground font-mono">
                            #{arch.cluster_id}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {arch.n_samples_in_cluster} samples
                        </span>
                      </div>
                      <CardTitle className="text-base">{archInfo.name}</CardTitle>
                      <CardDescription className="text-xs">{archInfo.behavior}</CardDescription>
                      
                      {/* Quality and Timing as simple text */}
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className={qualityStyle.text}>{qualityStyle.label}</span>
                        <span className="text-muted-foreground">{archInfo.timing}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CurveChart curve={arch.curve} color={qualityStyle.chartColor} />
                      <div className="flex justify-between text-xs text-muted-foreground mt-2">
                        <span>Start: {arch.m1?.toFixed(3)}</span>
                        <span>End: {arch.m12?.toFixed(3)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          
          {/* Table Tab */}
          <TabsContent value="table" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Archetype Values</CardTitle>
                <CardDescription>Monthly values (m1-m12) for each cluster center</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">ID</th>
                        <th className="text-left p-2 font-medium">Name</th>
                        <th className="text-left p-2 font-medium">Quality</th>
                        <th className="text-left p-2 font-medium">Timing</th>
                        <th className="text-right p-2 font-medium">N</th>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                          <th key={m} className="text-right p-2 font-medium">M{m}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {archetypes.map((arch) => {
                        const archInfo = getArchetypeInfo(arch.cluster_id);
                        const qualityStyle = QUALITY_STYLES[archInfo.quality];
                        return (
                          <tr key={arch.cluster_id} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-mono text-muted-foreground">{arch.cluster_id}</td>
                            <td className="p-2 font-medium">{archInfo.name}</td>
                            <td className="p-2">
                              <span className={`inline-flex items-center gap-1.5 ${qualityStyle.text}`}>
                                <span className={`w-2 h-2 rounded-full ${qualityStyle.dot}`}></span>
                                {qualityStyle.label}
                              </span>
                            </td>
                            <td className="p-2 text-muted-foreground">{archInfo.timing}</td>
                            <td className="text-right p-2">{arch.n_samples_in_cluster}</td>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                              <td key={m} className="text-right p-2 font-mono text-xs">
                                {arch[`m${m}`]?.toFixed(3)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
